import React, { useState, useEffect } from 'react'
import { Layout, Button, Space, Typography, Modal, List, notification } from 'antd'
import { PlusOutlined, SettingOutlined, PlayCircleOutlined, QuestionCircleOutlined, DollarOutlined, BorderOutlined, SaveOutlined, EyeOutlined, BulbOutlined, AimOutlined, SafetyCertificateOutlined, StarOutlined } from '@ant-design/icons'
import { Canvas, useThree } from '@react-three/fiber'
import { OrbitControls, Environment } from '@react-three/drei'
import { useDesignStore } from './store/designStore'
import GroundPlane from './components/Scene3D/GroundPlane'
import DesignedObjects from './components/Scene3D/DesignedObjects'
import MeasurementTools from './components/Scene3D/MeasurementTools'
import EnhancedLightingSystem from './components/Scene3D/lighting/EnhancedLightingSystem'
import EnvironmentSystem from './components/Scene3D/environment/EnvironmentSystem'
import RealisticRenderingSystem from './components/Scene3D/RealisticRenderingSystem'
import DesignToolsPanel from './components/UI/DesignToolsPanel'
import VisualizerWizard from './components/UI/VisualizerWizard'
import PricingPanel from './components/UI/PricingPanel'
import SaveLoadPanel from './components/UI/SaveLoadPanel'
import CanvasControlsPanel from './components/UI/CanvasControlsPanel'
import EnhancedRenderingPanel from './components/UI/EnhancedRenderingPanel'
import SmartPlacementPanel from './components/UI/SmartPlacementPanel'
import CompliancePanel from './components/UI/CompliancePanel'
import DesignIntelligencePanel from './components/UI/DesignIntelligencePanel'
import TerrainControlsPanel from './components/UI/TerrainControlsPanel'
import ImageTransformPanel from './components/UI/ImageTransformPanel'
import HeaderMenu from './components/UI/HeaderMenu'
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts'
import './App.css'

const { Header, Content } = Layout
const { Title } = Typography

// Component to properly set up OrbitControls with drag control integration
const SceneControls: React.FC = () => {
  const { gl, scene } = useThree()
  const { canvasSettings } = useDesignStore()
  
  // Calculate appropriate zoom limits based on canvas size
  const maxCanvasSize = Math.max(canvasSettings.width, canvasSettings.height)
  const minDistance = Math.max(5, maxCanvasSize * 0.2) // Minimum zoom based on canvas size
  const maxDistance = Math.max(50, maxCanvasSize * 1.5) // Maximum zoom based on canvas size
  
  return (
    <OrbitControls 
      enableDamping 
      dampingFactor={0.05} 
      enablePan={true}
      enableZoom={true}
      enableRotate={true}
      minDistance={minDistance}
      maxDistance={maxDistance}
      maxPolarAngle={Math.PI / 2.1}
      target={[0, 0, 0]}
      makeDefault
      ref={(ref) => {
        if (ref) {
          // Store reference for drag controls in multiple places for reliability
          const canvas = gl.domElement
          if (canvas) {
            (canvas as any).__orbitControls = ref
          }
          // Also store in scene userData as backup
          scene.userData.orbitControls = ref
          // Store globally for debugging
          ;(window as any).__orbitControls = ref
          console.log('OrbitControls reference stored with limits:', {
            minDistance,
            maxDistance,
            canvasSize: maxCanvasSize
          })
        }
      }}
    />
  )
}

function App() {
  const [showDesignTools, setShowDesignTools] = useState(false)
  const [showWizard, setShowWizard] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [showPricing, setShowPricing] = useState(false)
  const [showMeasurements, setShowMeasurements] = useState(false)
  const [showSaveLoad, setShowSaveLoad] = useState(false)
  const [showCanvasControls, setShowCanvasControls] = useState(false)
  const [showEnhancedRendering, setShowEnhancedRendering] = useState(false)
  const [showSmartPlacement, setShowSmartPlacement] = useState(false)
  const [showCompliance, setShowCompliance] = useState(false)
  const [showDesignIntelligence, setShowDesignIntelligence] = useState(false)
  const [showTerrainControls, setShowTerrainControls] = useState(false)
  const [showImageTransform, setShowImageTransform] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [showInteriorView, setShowInteriorView] = useState(false)
  
  // Phase 2: Enhanced rendering controls
  const [lightingPreset, setLightingPreset] = useState('midday')
  const [weatherCondition, setWeatherCondition] = useState('clear')
  const [season, setSeason] = useState('summer')
  const [timeOfDay, setTimeOfDay] = useState(12)
  const [enableAdvancedRendering, setEnableAdvancedRendering] = useState(true)
  
  const { addADUModel, addPoolModel, getAllObjects, canvasSettings, updateCanvasSettings, addPoolEquipment, addADUEquipment, poolModels, aduModels, syncStatus, loadDesign, toggleRealisticMode, toggle3DTerrain } = useDesignStore()
  const allObjects = getAllObjects()
  const hasObjects = allObjects.length > 0
  const isLoading = syncStatus === 'syncing'
  
  // URL parameter handling for auto-loading designs
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const designId = urlParams.get('designId')
    const designType = urlParams.get('designType')
    const autoLoad = urlParams.get('autoLoad')
    
    console.log('URL Parameters:', { designId, designType, autoLoad })
    
    if (designId && autoLoad === 'true') {
      console.log('Auto-loading design:', designId)
      // Load the specific design
      loadDesign(designId)
        .then(() => {
          console.log('Design loaded successfully:', designId)
          // Show success notification
          notification.success({
            message: 'Design Loaded Successfully',
            description: `Your ${designType?.toUpperCase()} design has been loaded and is ready for editing.`,
            placement: 'topRight',
            duration: 4
          })
          // Auto-open design tools panel for immediate editing
          setShowDesignTools(true)
        })
        .catch((error) => {
          console.error('Failed to load design:', error)
          // Show error notification
          notification.error({
            message: 'Failed to Load Design',
            description: 'Creating a new design instead. Your original design may not be available.',
            placement: 'topRight',
            duration: 6
          })
          // Fallback: Create a new design based on type
          if (designType === 'adu') {
            handleAddADU()
          } else if (designType === 'pool') {
            handleAddPool()
          }
        })
    }
  }, [loadDesign])
  
  // Debug logging
  console.log('App render:', { 
    hasObjects, 
    objectCount: allObjects.length, 
    aduCount: aduModels.length, 
    poolCount: poolModels.length,
    syncStatus,
    isLoading
  })

  // Enable keyboard shortcuts
  useKeyboardShortcuts()

  // Drop handlers for drag and drop from DesignToolsPanel
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    // Reset visual state
    const target = e.currentTarget as HTMLElement
    target.style.borderColor = 'transparent'
    target.style.backgroundColor = 'transparent'
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'))
      const { type, equipmentType } = data
      
      // Get drop position relative to canvas
      const rect = e.currentTarget.getBoundingClientRect()
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 40 // Scale to world coordinates
      const z = ((e.clientY - rect.top) / rect.height - 0.5) * 40
      
      // If in interior view, provide helpful room suggestions
      let roomSuggestion = ''
      if (showInteriorView && type === 'adu') {
        roomSuggestion = getSuggestedRoom(equipmentType)
      }
      
      console.log('Dropped:', { 
        type, 
        equipmentType, 
        position: [x, 0, z], 
        roomSuggestion,
        interiorView: showInteriorView 
      })
      
      if (type === 'adu') {
        // Find the first ADU to attach this equipment to
        const firstADU = aduModels[0]
        if (firstADU) {
          const equipmentName = equipmentType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
          
               addADUEquipment({
                 name: equipmentName,
                 type: 'ADUEquipment',
                 equipmentType: equipmentType as any,
                 position: [x, 0, z],
                 rotation: [0, 0, 0],
                 scale: [1, 1, 1],
                 dimensions: getDefaultEquipmentDimensions(equipmentType),
                 materials: {
                   primary: '#f7fafc',
                   secondary: '#4a5568',
                   accent: '#2d3748'
                 },
                 aduId: firstADU.id
               })
        }
      } else if (type === 'pool') {
        // Find the first Pool to attach this equipment to
        const firstPool = poolModels[0]
        if (firstPool) {
          const equipmentName = equipmentType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
          
          addPoolEquipment({
            name: equipmentName,
            type: 'PoolEquipment',
            equipmentType: equipmentType as any,
            position: [x, 0, z],
            rotation: [0, 0, 0],
            scale: [1, 1, 1],
            dimensions: getDefaultEquipmentDimensions(equipmentType),
            materials: {
              primary: '#2c5aa0',
              secondary: '#1a365d',
              accent: '#2b6cb0'
            },
            poolId: firstPool.id
          })
        }
      }
    } catch (error) {
      console.error('Error handling drop:', error)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    setIsDragOver(true)
  }

  // Helper function to get default dimensions for equipment
  const getDefaultEquipmentDimensions = (equipmentType: string) => {
    const dimensionsMap: Record<string, { width: number; height: number; depth: number }> = {
      toilet: { width: 1.5, height: 2.5, depth: 2 },
      sink_bathroom: { width: 2, height: 1, depth: 1.5 },
      sink_kitchen: { width: 2.5, height: 1, depth: 2 },
      shower: { width: 3, height: 7, depth: 3 },
      bathtub: { width: 5, height: 2, depth: 2.5 },
      refrigerator: { width: 2.5, height: 6, depth: 2.5 },
      stove: { width: 2.5, height: 3, depth: 2 },
      dishwasher: { width: 2, height: 3, depth: 2 },
      microwave: { width: 2, height: 1, depth: 1.5 },
      kitchen_cabinets: { width: 8, height: 3, depth: 2 },
      bathroom_vanity: { width: 4, height: 3, depth: 2 },
      closet_system: { width: 6, height: 8, depth: 2 },
      window_standard: { width: 3, height: 4, depth: 0.5 },
      window_large: { width: 6, height: 4, depth: 0.5 },
      entry_door: { width: 3, height: 7, depth: 0.5 },
      interior_door: { width: 2.5, height: 7, depth: 0.5 },
      hvac_unit: { width: 3, height: 3, depth: 2 },
      adu_electrical_panel: { width: 1.5, height: 3, depth: 0.5 },
      water_heater: { width: 2, height: 4, depth: 2 },
      ceiling_light: { width: 1, height: 0.5, depth: 1 },
      recessed_light: { width: 0.5, height: 0.5, depth: 0.5 },
      outlet: { width: 0.5, height: 0.5, depth: 0.2 },
      pump: { width: 2, height: 2, depth: 1.5 },
      filter: { width: 2.5, height: 3, depth: 2 },
      heater: { width: 3, height: 2, depth: 2 },
      pool_electrical_panel: { width: 2, height: 3, depth: 1 },
      chlorinator: { width: 1.5, height: 2, depth: 1 },
      automation_controller: { width: 1, height: 1.5, depth: 0.5 }
    }
    
    return dimensionsMap[equipmentType] || { width: 1, height: 1, depth: 1 }
  }

  // Helper function to suggest appropriate room for equipment
  const getSuggestedRoom = (equipmentType: string): string => {
    const roomSuggestions: Record<string, string> = {
      toilet: 'Bathroom',
      sink_bathroom: 'Bathroom',
      bathtub: 'Bathroom',
      shower: 'Bathroom',
      bathroom_vanity: 'Bathroom',
      sink_kitchen: 'Kitchen',
      refrigerator: 'Kitchen',
      stove: 'Kitchen',
      dishwasher: 'Kitchen',
      microwave: 'Kitchen',
      kitchen_cabinets: 'Kitchen',
      window_standard: 'Any Room',
      window_large: 'Living Room',
      entry_door: 'Entrance',
      interior_door: 'Between Rooms',
      hvac_unit: 'Utility Area',
      electrical_panel: 'Utility Area',
      water_heater: 'Utility Area',
      ceiling_light: 'Any Room',
      recessed_light: 'Any Room',
      outlet: 'Any Room',
      closet_system: 'Bedroom'
    }
    return roomSuggestions[equipmentType] || 'Any Room'
  }

  const handleAddADU = () => {
    addADUModel({
      type: 'ADU',
      name: 'New ADU',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      dimensions: { width: 24, height: 9, depth: 28 },
      materials: { walls: '#ffffff', roof: '#808080', trim: '#cccccc', door: '#8b4513', windows: '#87ceeb' },
      features: { bedrooms: 1, bathrooms: 1, kitchen: true, livingRoom: true, dining: false, office: false, storage: true, laundry: false, parking: false, porch: false, balcony: false, fireplace: false },
      sections: [],
      isModular: false
    })
    setShowDesignTools(true)
  }

  const handleAddPool = () => {
    addPoolModel({
      type: 'Pool',
      name: 'New Pool',
      position: [5, 0, 5],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      dimensions: { width: 15, depth: 8, length: 20 },
      materials: { 
        water: '#6dd5ed', 
        shell: 'gunite',
        interior: 'plaster',
        interiorColor: '#ffffff',
        coping: 'concrete',
        copingColor: '#a0a0a0',
        deck: 'concrete',
        deckColor: '#deb887'
      },
      features: { 
        spa: true, 
        waterfall: false,
        fountains: false,
        lighting: true, 
        lightingType: 'led',
        lightingColor: 'white',
        heating: false,
        heatingType: 'gas',
        cooling: false,
        filtration: 'sand',
        automation: 'basic',
        cleaner: 'suction',
        sanitization: 'chlorine',
        safety: true,
        safetyType: 'fence',
        cover: false,
        sound: false,
        wifi: false,
        excavation: 'standard',
        access: 'easy'
      },
      shape: 'rectangular'
    })
    setShowDesignTools(true)
  }

  return (
    <Layout className="ant-layout">
      <Header className="ant-layout-header" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        background: '#fff', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        zIndex: 1000,
        padding: '0 24px'
      }}>
        <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
          🏠 ServiceFlow Pro.ai
          {isLoading && (
            <span style={{ marginLeft: '12px', fontSize: '14px', color: '#fa8c16' }}>
              🔄 Loading Design...
            </span>
          )}
        </Title>
        
        <Space size="large">
          {/* Quick Action Buttons */}
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddADU} size="large">
            Add ADU
          </Button>
          <Button icon={<PlusOutlined />} onClick={handleAddPool} size="large">
            Add Pool
          </Button>
          
          {/* Main Menu */}
          <HeaderMenu
            hasObjects={hasObjects}
            showInteriorView={showInteriorView}
            showMeasurements={showMeasurements}
            showSaveLoad={showSaveLoad}
            showCanvasControls={showCanvasControls}
            showEnhancedRendering={showEnhancedRendering}
            showSmartPlacement={showSmartPlacement}
            showCompliance={showCompliance}
            showDesignIntelligence={showDesignIntelligence}
                  realisticModeEnabled={canvasSettings.enableRealisticMode || false}
                  terrain3DEnabled={canvasSettings.enable3DTerrain || false}
                  hasBackgroundImage={!!canvasSettings.backgroundImage}
                  onStartWizard={() => setShowWizard(true)}
            onAddADU={handleAddADU}
            onAddPool={handleAddPool}
            onToggleDesignTools={() => setShowDesignTools(!showDesignTools)}
            onTogglePricing={() => setShowPricing(!showPricing)}
            onToggleMeasurements={() => setShowMeasurements(!showMeasurements)}
            onToggleSaveLoad={() => setShowSaveLoad(!showSaveLoad)}
            onToggleCanvasControls={() => setShowCanvasControls(!showCanvasControls)}
            onToggleInteriorView={() => setShowInteriorView(!showInteriorView)}
            onToggleEnhancedRendering={() => setShowEnhancedRendering(!showEnhancedRendering)}
            onToggleSmartPlacement={() => setShowSmartPlacement(!showSmartPlacement)}
            onToggleCompliance={() => setShowCompliance(!showCompliance)}
            onToggleDesignIntelligence={() => setShowDesignIntelligence(!showDesignIntelligence)}
            onToggleRealisticMode={toggleRealisticMode}
                  onToggle3DTerrain={() => {
                    toggle3DTerrain()
                    setShowTerrainControls(!canvasSettings.enable3DTerrain)
                  }}
                  onShowImageTransform={() => setShowImageTransform(true)}
                  onShowHelp={() => setShowHelp(true)}
          />
        </Space>
      </Header>
      
      <Content className="ant-layout-content" style={{ 
        position: 'relative',
        background: hasObjects ? '#87CEEB' : '#f0f2f5'
      }}>
        {showWizard && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }}>
              <VisualizerWizard onFinish={() => setShowWizard(false)} />
            </div>
          </div>
        )}
        
        {hasObjects || isLoading ? (
          <div 
            className="canvas-container"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{ 
              position: 'relative',
              border: '2px dashed transparent',
              transition: 'border-color 0.3s ease'
            }}
            onDragEnter={(e) => {
              e.preventDefault()
              e.currentTarget.style.borderColor = '#1890ff'
              e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.05)'
              setIsDragOver(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              e.currentTarget.style.borderColor = 'transparent'
              e.currentTarget.style.backgroundColor = 'transparent'
              setIsDragOver(false)
            }}
          >
            <Canvas camera={{ position: [15, 15, 15], fov: 50 }} shadows>
              {/* Realistic Rendering System - overrides other lighting when enabled */}
              {canvasSettings.enableRealisticMode ? (
                <RealisticRenderingSystem>
                  <Environment preset="city" background={!canvasSettings.backgroundImage} />
                </RealisticRenderingSystem>
              ) : enableAdvancedRendering ? (
                <>
                  <EnhancedLightingSystem 
                    preset={lightingPreset}
                    timeOfDay={timeOfDay}
                    enableShadows={true}
                    enableFog={true}
                    enableHDRI={false}
                  />
                  <EnvironmentSystem
                    weather={weatherCondition}
                    season={season}
                    timeOfDay={timeOfDay}
                    enableWeatherEffects={true}
                    enableSeasonalChanges={true}
                    enableAtmosphericEffects={true}
                  />
                </>
              ) : (
                // Fallback to basic lighting for performance
                <>
                  <ambientLight intensity={0.6} />
                  <pointLight position={[10, 10, 10]} intensity={1} />
                  <pointLight position={[-10, 10, -10]} intensity={0.7} />
                  <hemisphereLight args={["#ffffff", "#444444", 0.4]} />
                  <Environment preset="sunset" background={!canvasSettings.backgroundImage} />
                </>
              )}
              
              <GroundPlane />
              <DesignedObjects showInteriorView={showInteriorView} />
              <MeasurementTools showMeasurements={showMeasurements} />

              <SceneControls />
            </Canvas>
            
            {/* Loading indicator */}
            {isLoading && !hasObjects && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                padding: '20px 40px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 'bold',
                textAlign: 'center',
                pointerEvents: 'none',
                zIndex: 1000,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
              }}>
                🔄 Loading Design...
              </div>
            )}

            {/* Drop instruction overlay */}
            {isDragOver && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(24, 144, 255, 0.9)',
                color: 'white',
                padding: '20px 40px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 'bold',
                textAlign: 'center',
                pointerEvents: 'none',
                zIndex: 1000,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
              }}>
                {showInteriorView ? (
                  <>
                    🏠 Drop in the appropriate room<br/>
                    <span style={{ fontSize: '14px', fontWeight: 'normal' }}>
                      Color-coded zones show room types
                    </span>
                  </>
                ) : (
                  '🎯 Drop here to place component'
                )}
              </div>
            )}

            {/* Room Legend for Interior View */}
            {showInteriorView && (
              <div style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                background: 'rgba(255, 255, 255, 0.95)',
                padding: '16px',
                borderRadius: '8px',
                fontSize: '12px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                zIndex: 1000
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>🏠 Room Guide</div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <div style={{ width: '12px', height: '12px', background: '#48bb78', marginRight: '8px', borderRadius: '2px' }}></div>
                  Living Room
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <div style={{ width: '12px', height: '12px', background: '#ed8936', marginRight: '8px', borderRadius: '2px' }}></div>
                  Kitchen
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <div style={{ width: '12px', height: '12px', background: '#4299e1', marginRight: '8px', borderRadius: '2px' }}></div>
                  Bedroom
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <div style={{ width: '12px', height: '12px', background: '#9f7aea', marginRight: '8px', borderRadius: '2px' }}></div>
                  Bathroom
                </div>
              </div>
            )}
            
            {showDesignTools && (
              <div className="design-tools-panel">
                <DesignToolsPanel onClose={() => setShowDesignTools(false)} />
              </div>
            )}
            
            {showPricing && (
              <div className="design-tools-panel" style={{ right: showDesignTools ? '370px' : '20px' }}>
                <PricingPanel onClose={() => setShowPricing(false)} />
              </div>
            )}
            
                   {showSaveLoad && (
                     <div className="design-tools-panel" style={{
                       right: (showDesignTools && showPricing) ? '740px' :
                              (showDesignTools || showPricing) ? '370px' : '20px'
                     }}>
                       <SaveLoadPanel />
                     </div>
                   )}

                   {showCanvasControls && (
                     <div className="design-tools-panel" style={{
                       right: (showDesignTools && showPricing && showSaveLoad) ? '1110px' :
                              (showDesignTools && showPricing) || (showDesignTools && showSaveLoad) || (showPricing && showSaveLoad) ? '740px' :
                              (showDesignTools || showPricing || showSaveLoad) ? '370px' : '20px'
                     }}>
                       <CanvasControlsPanel />
                     </div>
                   )}

                   {/* Enhanced Rendering Panel */}
                   {showEnhancedRendering && (
                     <EnhancedRenderingPanel
                       lightingPreset={lightingPreset}
                       weatherCondition={weatherCondition}
                       season={season}
                       timeOfDay={timeOfDay}
                       enableAdvancedRendering={enableAdvancedRendering}
                       onLightingPresetChange={setLightingPreset}
                       onWeatherConditionChange={setWeatherCondition}
                       onSeasonChange={setSeason}
                       onTimeOfDayChange={setTimeOfDay}
                       onAdvancedRenderingToggle={setEnableAdvancedRendering}
                       onClose={() => setShowEnhancedRendering(false)}
                     />
                   )}

                   {/* Smart Placement Panel */}
                   {showSmartPlacement && (
                     <SmartPlacementPanel
                       onClose={() => setShowSmartPlacement(false)}
                     />
                   )}

                   {/* Compliance Panel */}
                   {showCompliance && (
                     <CompliancePanel
                       onClose={() => setShowCompliance(false)}
                     />
                   )}

                   {/* Design Intelligence Panel */}
                  {showDesignIntelligence && (
                    <DesignIntelligencePanel
                      onClose={() => setShowDesignIntelligence(false)}
                    />
                  )}

                  {showTerrainControls && (
                    <TerrainControlsPanel
                      visible={showTerrainControls}
                      onClose={() => setShowTerrainControls(false)}
                    />
                  )}

                {/* Image Transform Panel */}
                <ImageTransformPanel
                  visible={showImageTransform}
                  onClose={() => setShowImageTransform(false)}
                />
              </div>
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div className="welcome-card">
              <Title level={2}>🎯 3D Visualizer Ready!</Title>
              <p style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
                Click "Add ADU" or "Add Pool" to start designing your project.
                The 3D scene will load once you add your first object.
              </p>
              <Space size="large">
                <Button size="large" type="primary" icon={<PlusOutlined />} onClick={handleAddADU}>
                  Add Your First ADU
                </Button>
                <Button size="large" icon={<PlusOutlined />} onClick={handleAddPool}>
                  Add Your First Pool
                </Button>
              </Space>
            </div>
          </div>
        )}

        {/* Help Modal */}
        <Modal
          title="🎯 Visualizer Help & Shortcuts"
          open={showHelp}
          onCancel={() => setShowHelp(false)}
          footer={[
            <Button key="close" type="primary" onClick={() => setShowHelp(false)}>
              Got it!
            </Button>
          ]}
          width={600}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Title level={4}>🎮 How to Use</Title>
              <List size="small">
                <List.Item>• Click "Start Wizard" for a guided setup experience</List.Item>
                <List.Item>• Use "Add ADU" or "Add Pool" to quickly add objects</List.Item>
                <List.Item>• Click objects in the 3D scene to select them</List.Item>
                <List.Item>• Use "Design Tools" to customize selected objects</List.Item>
                <List.Item>• Mouse: Left-click drag to orbit, scroll to zoom, right-click drag to pan</List.Item>
              </List>
            </div>

            <div>
              <Title level={4}>⌨️ Keyboard Shortcuts</Title>
              <List size="small">
                <List.Item><strong>Delete/Backspace:</strong> Delete selected objects</List.Item>
                <List.Item><strong>Ctrl/Cmd + Z:</strong> Undo last action</List.Item>
                <List.Item><strong>Ctrl/Cmd + Shift + Z:</strong> Redo action</List.Item>
                <List.Item><strong>Ctrl/Cmd + Y:</strong> Redo action (alternative)</List.Item>
                <List.Item><strong>Escape:</strong> Clear selection</List.Item>
              </List>
            </div>

            <div>
              <Title level={4}>🏠 Object Types</Title>
              <List size="small">
                <List.Item><strong>ADU (Accessory Dwelling Unit):</strong> Customizable living spaces with bedrooms, bathrooms, kitchen options</List.Item>
                <List.Item><strong>Pool:</strong> Swimming pools with optional spa, lighting, and heating features</List.Item>
              </List>
            </div>

            <div>
              <Title level={4}>🎨 Design Tools</Title>
              <List size="small">
                <List.Item>• <strong>Position:</strong> Move objects in X, Y, Z coordinates</List.Item>
                <List.Item>• <strong>Rotation:</strong> Rotate objects around each axis</List.Item>
                <List.Item>• <strong>Scale:</strong> Resize objects uniformly</List.Item>
                <List.Item>• <strong>Materials:</strong> Change colors and textures</List.Item>
                <List.Item>• <strong>Features:</strong> Toggle specific object features</List.Item>
              </List>
            </div>
          </Space>
        </Modal>
      </Content>
    </Layout>
  )
}

export default App